import { addYears, formatISO } from 'date-fns';
import type { FormError } from 'models/formError';
import type { Collection } from 'state/utils/types';
import { now } from 'utils/core/now';
import { nowISODateString } from 'utils/date';
import type { HttpErrorType } from 'utils/http/httpClient.model';
import type { UppercaseLoadingStatus } from 'utils/status';
export type DateMode = 'range' | 'multi';

export type TimeSerie = Serie<number>;
export type PeriodSerie = Serie<string>;
export type Serie<T> = { quoteId: string; data: readonly Point<T>[] };
export type Point<T> = [T, number];

type Statistic = number | null | undefined;
export type Statistics = Record<string, Statistic[]>;

type GetBackTestResultStatus = `GET_BACK_TEST_${UppercaseLoadingStatus}`;
type RequestBackTestStatus = `RUN_BACK_TEST_${UppercaseLoadingStatus}`;
type CancelBackTestStatus = `CANCEL_BACK_TEST`;

export type BackTestStatus = GetBackTestResultStatus | RequestBackTestStatus | CancelBackTestStatus;

export type QuoteIds = { initialQuoteId: string; secondQuoteId: string | undefined | null };
export type QuoteLabels = { initialQuoteLabel?: string; secondQuoteLabel?: string };

type BackTestData = {
  statistics: Statistics;
  irrEvolutionSeries: readonly TimeSerie[];
  cumulatedAutocallProbSeries: readonly PeriodSerie[];
};
export type ExpandedData = { [Property in keyof BackTestData]: boolean };
type ErrorStatus = 'INTERNAL' | 'BAD_DATA' | 'NOT_FOUND';
export type ErrorByQuoteId = Record<string, { status: ErrorStatus; message?: string }>;
export type BackTestErrorModel = Pick<BackTestModel, 'quoteIds' | 'maturities'>;
export type BackTestFormError = FormError<BackTestErrorModel>;
export type BackTestModel = {
  backTestId?: string;
  status: BackTestStatus;
  errors?: ErrorByQuoteId;
  quoteIds: QuoteIds;
  quoteLabels: QuoteLabels;
  requestedQuoteIds: QuoteIds;
  requestedQuoteLabels: QuoteLabels;
  errorMessage?: string;
  timeout?: number;
  maturities: readonly string[];
  canAddExtraQuote: boolean;

  canRunBackTest: boolean;

  validationErrors: BackTestFormError[];

  maturityMode: DateMode;

  errorType?: HttpErrorType;

  expandedData: ExpandedData;
} & BackTestData;

export type BackTestByTabIdState = Collection<BackTestModel>;
export const initialBackTestByTabId = {};

export const tenYearsAgoISODate = formatISO(addYears(now(), -10), { representation: 'date' });
export const nowISODate = nowISODateString();
export const initialBackTest = (initialQuoteId: string) =>
  ({
    irrEvolutionSeries: [],
    cumulatedAutocallProbSeries: [],
    statistics: {},
    quoteIds: { initialQuoteId, secondQuoteId: undefined },
    quoteLabels: {},
    requestedQuoteLabels: {},
    requestedQuoteIds: { initialQuoteId, secondQuoteId: undefined },
    status: 'RUN_BACK_TEST_IDLE',
    maturities: [tenYearsAgoISODate, nowISODate],
    canAddExtraQuote: true,
    canRunBackTest: true,
    validationErrors: [],
    maturityMode: 'range',
    expandedData: { cumulatedAutocallProbSeries: false, irrEvolutionSeries: false, statistics: true },
  }) satisfies BackTestModel;
